import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, withLatestFrom } from 'rxjs';
import { catchError, map, mapTo, switchMap, tap } from 'rxjs/operators';
import { RouterState } from '@ngrx/router-store';
import { setCxbSuccessNotification } from '../notification/notification.actions';
import { apiErrorMessage } from '../shared/functions/general-api-error-handling';
import { selectCxbSelectedTenant } from '../app.selectors';
import * as Action from './create-tray-screen.actions';
import {
  createCxbPageItem,
  createCxbPageItemFail,
  createCxbPageItemSuccess,
  deleteCxbPageItem,
  deleteCxbPageItemFail,
  deleteCxbPageItemSuccess,
  loadCxbPageItem,
  loadCxbPageItemFail,
  loadCxbPageItemSuccess,
  updateCxbPageItem,
  updateCxbPageItemFail,
  updateCxbPageItemSuccess,
} from './create-tray-screen.actions';
import { RootAppState } from './../app.reducer';
import { TrayApiCreateService } from './tray-api-create.service';
import { TrayApiLoadService } from './tray-api-load.service';
import { TrayApiUpdateService } from './tray-api-update.service';
import { TrayApiDeleteService } from './tray-api-delete.service';

@Injectable()
export class CreateTrayScreenEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly trayApiCreateService: TrayApiCreateService,
    private readonly trayApiLoadService: TrayApiLoadService,
    private readonly trayApiUpdateService: TrayApiUpdateService,
    private readonly trayApiDeleteService: TrayApiDeleteService,
    private readonly rootStore: Store<RootAppState & RouterState>
  ) {}

  public createSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Action.createCxbPageItemSuccess),
      tap(({ pageVersionId, pageType }) => this.router.navigate([pageType.routePath, pageVersionId])),
      mapTo(setCxbSuccessNotification({ message: 'Successfully created the tray' }))
    )
  );

  public updateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Action.updateCxbPageItemSuccess),
      tap(({ pageVersionId, pageType }) => this.router.navigate([pageType.routePath, pageVersionId])),
      mapTo(setCxbSuccessNotification({ message: 'Successfully updated the tray' }))
    )
  );

  public deleteSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Action.deleteCxbPageItemSuccess),
      tap(({ pageVersionId, pageType }) => this.router.navigate([pageType.routePath, pageVersionId])),
      mapTo(setCxbSuccessNotification({ message: 'Successfully deleted the tray' }))
    )
  );

  public readonly createTrayEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createCxbPageItem),
      withLatestFrom(this.rootStore.select(selectCxbSelectedTenant)),
      switchMap(([actionProps, selectedTenant]) =>
        this.trayApiCreateService
          .createPageItem$(actionProps.pageItem, actionProps.pageVersionId, actionProps.pageType, selectedTenant)
          .pipe(
            map(() => createCxbPageItemSuccess({ pageVersionId: actionProps.pageVersionId, pageType: actionProps.pageType })),
            catchError((error) => {
              console.error(error);
              return of(createCxbPageItemFail(apiErrorMessage(error)));
            })
          )
      )
    )
  );

  public readonly loadTrayEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCxbPageItem),
      withLatestFrom(this.rootStore.select(selectCxbSelectedTenant)),
      switchMap(([actionProps, selectedTenant]) =>
        this.trayApiLoadService.loadPageItem$(actionProps.id, actionProps.discriminatorType, actionProps.pageType, selectedTenant).pipe(
          map((pageItem) => loadCxbPageItemSuccess({ pageItem, pageType: actionProps.pageType })),
          catchError((error) => {
            console.error(error);
            return of(loadCxbPageItemFail(apiErrorMessage(error)));
          })
        )
      )
    )
  );

  public readonly updateTrayEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCxbPageItem),
      withLatestFrom(this.rootStore.select(selectCxbSelectedTenant)),
      switchMap(([actionProps, selectedTenant]) =>
        this.trayApiUpdateService
          .updatePageItem$(actionProps.pageItem, actionProps.pageVersionId, actionProps.pageType, selectedTenant)
          .pipe(
            map(() => updateCxbPageItemSuccess({ pageVersionId: actionProps.pageVersionId, pageType: actionProps.pageType })),
            catchError((error) => {
              console.error(error);
              return of(updateCxbPageItemFail(apiErrorMessage(error)));
            })
          )
      )
    )
  );

  public readonly deleteTrayEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteCxbPageItem),
      withLatestFrom(this.rootStore.select(selectCxbSelectedTenant)),
      switchMap(([actionProps, selectedTenant]) =>
        this.trayApiDeleteService.deletePageItem$(actionProps.id, actionProps.trayType, actionProps.pageType, selectedTenant).pipe(
          map(() => deleteCxbPageItemSuccess({ pageVersionId: actionProps.pageVersionId, pageType: actionProps.pageType })),
          catchError((error) => {
            console.error(error);
            return of(deleteCxbPageItemFail(apiErrorMessage(error)));
          })
        )
      )
    )
  );
}
